/* Created by Interactive 63 - https://interactive63.com */
/**
 * Point editing on the stage: shows a path's points and curve handles, and moves, adds and
 * deletes them. When the layer has a shape morph track, edits key the shape at the playhead;
 * otherwise they change the artwork's own geometry. Each drag is one undo step.
 */
import {
  convertShapeInSource,
  originalPathData,
  setPathDataInSource,
} from '../../../core/morph/convert';
import { isMorphValue, type ElementId, type MorphValue } from '../../../core/model/types';
import {
  deleteNodes,
  formatEditable,
  insertNode,
  moveNodes,
  nearestOnSegment,
  nodeAt,
  parseEditable,
  segmentPoint,
  segmentsOf,
  setHandle,
  toggleSmooth,
  type EditablePath,
  type NodeRef,
  type SegmentRef,
} from '../../../core/path/edit';
import { findTrack } from '../../../core/timeline/keyframes';
import { sampleTrack } from '../../../core/timeline/sample';
import { setPropertyValue } from '../../store/property-actions';
import type { Store } from '../../store/store';
import { toast } from '../../ui/toast';
import { applyLinear, applyMatrix, invertMatrix, type Matrix, type Point } from './geometry';

/** Points drawn along each segment for the outline and its hit area. */
const SEGMENT_SAMPLES = 24;
/** Movement before a press on a point becomes a drag rather than a click. */
const DRAG_THRESHOLD_PX = 3;
/** Arrow key nudges, in the path's own units. */
const NUDGE = 1;
const NUDGE_LARGE = 10;

export interface PointEditData {
  anchors: { ref: NodeRef; point: Point; selected: boolean; smooth: boolean }[];
  handles: { ref: NodeRef; side: 'in' | 'out'; anchor: Point; end: Point }[];
  segments: { ref: SegmentRef; points: Point[] }[];
}

/** What point editing needs from the stage. */
export interface PointEditServices {
  store: Store;
  /** The rendered element whose path data is edited. */
  getElement(id: ElementId): Element | undefined;
  /** An element's matrix to the screen, including the stage zoom. */
  screenMatrix(node: SVGGraphicsElement): Matrix | null;
  /** The time that morph edits are keyed at. */
  time(): number;
  /** Redraws the overlay and the editing banner. */
  pointEditChanged(): void;
}

/** Whether two point references name the same point. */
function same(a: NodeRef, b: NodeRef): boolean {
  return a.subpath === b.subpath && a.node === b.node;
}

/** A short random key so the updates of one drag coalesce into one undo step. */
function dragKey(): string {
  return `points:${Math.random().toString(36).slice(2)}`;
}

export class PointEditor {
  private elementId: ElementId | null = null;
  private selected: NodeRef[] = [];

  /** Creates the editor; it does nothing until a layer is entered. */
  constructor(private readonly svc: PointEditServices) {}

  /** The layer being edited, or null. */
  get editingId(): ElementId | null {
    return this.elementId;
  }

  /**
   * Starts editing a layer's points. Basic shapes are converted to paths first, after asking.
   * Returns whether editing started.
   */
  enter(id: ElementId): boolean {
    const { project } = this.svc.store.getState();
    const meta = project.elements[id];
    if (!meta) return false;
    if (meta.tag !== 'path') {
      const converted = convertShapeInSource(project.svg.source, id);
      if (!converted) {
        toast.info('Only shapes and paths have points to edit.');
        return false;
      }
      if (
        !window.confirm(
          `Convert ${meta.label} to a path to edit its points? Its appearance won't change.`,
        )
      ) {
        return false;
      }
      this.svc.store.updateProject('Convert to path', (draft) => {
        draft.svg.source = converted.source;
        const element = draft.elements[id];
        if (element) {
          element.tag = 'path';
          delete element.pathLength;
        }
      });
    }
    if (!this.path(id)) {
      toast.info("This path's data couldn't be read.");
      return false;
    }
    this.elementId = id;
    this.selected = [];
    this.svc.pointEditChanged();
    return true;
  }

  /** Stops editing. */
  exit(): void {
    if (this.elementId === null) return;
    this.elementId = null;
    this.selected = [];
    this.svc.pointEditChanged();
  }

  /** Whether the layer has a shape morph track, so edits key its shape. */
  keysMorph(id = this.elementId): boolean {
    if (id === null) return false;
    return (findTrack(this.svc.store.getState().project, id, 'morph')?.keyframes.length ?? 0) > 0;
  }

  /** The morph value showing at the playhead, when the layer is morphing. */
  private morphValue(id: ElementId): MorphValue | undefined {
    const { project } = this.svc.store.getState();
    const track = findTrack(project, id, 'morph');
    if (!track || track.keyframes.length === 0) return undefined;
    const value = sampleTrack(track, this.svc.time(), project.scene.duration);
    return isMorphValue(value) ? value : undefined;
  }

  /** The path being edited, as it is at the playhead. */
  private path(id = this.elementId): EditablePath | null {
    if (id === null) return null;
    const d =
      this.morphValue(id)?.d ?? originalPathData(this.svc.store.getState().project.svg.source, id);
    return parseEditable(d);
  }

  /** Writes an edited path: a morph keyframe at the playhead, or the artwork's own geometry. */
  private write(path: EditablePath, coalesceKey: string): void {
    const id = this.elementId;
    if (id === null) return;
    const d = formatEditable(path);
    const morph = this.morphValue(id);
    if (morph) {
      const outcome = setPropertyValue(
        this.svc.store,
        [id],
        'morph',
        { ...morph, d },
        { time: this.svc.time(), coalesceKey },
      );
      if (outcome === 'rejected')
        toast.info('Auto-key is off. Click the diamond to add a keyframe.');
      return;
    }
    this.svc.store.updateProject(
      'Edit points',
      (draft) => {
        const next = setPathDataInSource(draft.svg.source, id, d);
        if (next === null) return;
        draft.svg.source = next;
        const element = draft.elements[id];
        if (element) delete element.pathLength;
      },
      { coalesceKey },
    );
  }

  /** The matrix from the path's own coordinates to the screen. */
  private matrix(): Matrix | null {
    const el = this.elementId === null ? undefined : this.svc.getElement(this.elementId);
    return el ? this.svc.screenMatrix(el as SVGGraphicsElement) : null;
  }

  /** The points, handles and segments to draw, in client coordinates. */
  scene(): PointEditData | null {
    const path = this.path();
    const m = this.matrix();
    if (!path || !m) return null;
    const screen = (x: number, y: number): Point => applyMatrix(m, { x, y });
    const anchors: PointEditData['anchors'] = [];
    const handles: PointEditData['handles'] = [];
    path.subpaths.forEach((sub, s) => {
      sub.nodes.forEach((node, n) => {
        const ref = { subpath: s, node: n };
        const point = screen(node.x, node.y);
        const selected = this.selected.some((r) => same(r, ref));
        anchors.push({ ref, point, selected, smooth: node.smooth });
        if (!selected) return;
        for (const side of ['in', 'out'] as const) {
          const offset = node[side];
          if (offset) {
            handles.push({
              ref,
              side,
              anchor: point,
              end: screen(node.x + offset.x, node.y + offset.y),
            });
          }
        }
      });
    });
    const segments = segmentsOf(path).map((ref) => ({
      ref,
      points: Array.from({ length: SEGMENT_SAMPLES + 1 }, (_, i) => {
        const p = segmentPoint(path, ref, i / SEGMENT_SAMPLES) ?? { x: 0, y: 0 };
        return screen(p.x, p.y);
      }),
    }));
    return { anchors, handles, segments };
  }

  /** Follows the pointer from a press, reporting movement in the path's own units. */
  private track(
    down: PointerEvent,
    onMove: (dx: number, dy: number, event: PointerEvent) => void,
    onClick: () => void,
  ): void {
    const m = this.matrix();
    const inverse = m ? invertMatrix(m) : null;
    if (!inverse) return;
    let started = false;
    const move = (event: PointerEvent): void => {
      const sx = event.clientX - down.clientX;
      const sy = event.clientY - down.clientY;
      if (!started && Math.hypot(sx, sy) < DRAG_THRESHOLD_PX) return;
      started = true;
      const local = applyLinear(inverse, { x: sx, y: sy });
      onMove(local.x, local.y, event);
    };
    const end = (): void => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', end);
      window.removeEventListener('pointercancel', end);
      if (!started) onClick();
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', end);
    window.addEventListener('pointercancel', end);
  }

  /**
   * A press on a point: Shift adds or removes it from the selection, Alt switches it between
   * smooth and corner, and a drag moves every selected point.
   */
  beginPointDrag(ref: NodeRef, down: PointerEvent): void {
    const start = this.path();
    if (!start) return;
    if (down.altKey) {
      this.write(toggleSmooth(start, ref), dragKey());
      return;
    }
    const isSelected = this.selected.some((r) => same(r, ref));
    if (down.shiftKey) {
      this.selected = isSelected
        ? this.selected.filter((r) => !same(r, ref))
        : [...this.selected, ref];
      this.svc.pointEditChanged();
      return;
    }
    if (!isSelected) {
      this.selected = [ref];
      this.svc.pointEditChanged();
    }
    const moving = [...this.selected];
    const key = dragKey();
    this.track(
      down,
      (dx, dy) => {
        this.write(moveNodes(start, moving, dx, dy), key);
      },
      () => {
        this.selected = [ref];
        this.svc.pointEditChanged();
      },
    );
  }

  /** Drags a curve handle; Alt breaks a smooth point into a corner. */
  beginHandleDrag(ref: NodeRef, side: 'in' | 'out', down: PointerEvent): void {
    const start = this.path();
    const offset = start ? nodeAt(start, ref)?.[side] : undefined;
    if (!start || !offset) return;
    const key = dragKey();
    this.track(
      down,
      (dx, dy, event) => {
        const value = { x: offset.x + dx, y: offset.y + dy };
        this.write(setHandle(start, ref, side, value, event.altKey || down.altKey), key);
      },
      () => undefined,
    );
  }

  /** Adds a point on a segment where it was double-clicked. */
  insertAt(seg: SegmentRef, clientX: number, clientY: number): void {
    const path = this.path();
    const m = this.matrix();
    const inverse = m ? invertMatrix(m) : null;
    if (!path || !inverse) return;
    const local = applyMatrix(inverse, { x: clientX, y: clientY });
    const added = insertNode(path, seg, nearestOnSegment(path, seg, local));
    if (!added) return;
    this.write(added.path, dragKey());
    this.selected = [added.ref];
    this.svc.pointEditChanged();
  }

  /** Deletes the selected points, explaining when a part would be left too small. */
  deleteSelected(): void {
    const path = this.path();
    if (!path || this.selected.length === 0) return;
    const result = deleteNodes(path, this.selected);
    if ('reason' in result) {
      toast.info(result.reason);
      return;
    }
    this.write(result.path, dragKey());
    this.selected = [];
    this.svc.pointEditChanged();
  }

  /** Moves the selected points by whole units, or tens with Shift. */
  nudge(dx: number, dy: number, large: boolean, coalesceKey: string): void {
    const path = this.path();
    if (!path || this.selected.length === 0) return;
    const step = large ? NUDGE_LARGE : NUDGE;
    this.write(moveNodes(path, this.selected, dx * step, dy * step), coalesceKey);
  }

  /**
   * Starts animating the shape: keys the current geometry at the start of the scene, so later
   * edits key the shape at the playhead.
   */
  keyShape(): void {
    const id = this.elementId;
    if (id === null || this.keysMorph(id)) return;
    const d = originalPathData(this.svc.store.getState().project.svg.source, id);
    if (d === '') return;
    setPropertyValue(this.svc.store, [id], 'morph', { d }, { time: 0 });
    this.svc.pointEditChanged();
  }

  /** Whether any points are selected. */
  hasSelection(): boolean {
    return this.selected.length > 0;
  }
}
